import { useSynchronizedRef } from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import {
	ReadonlyRefObject,
	useCallback,
	useEffect
} from "react";

export function useOnCurrentWindowBlur(
	callback?: () => void
): void {
	const callbackRef: ReadonlyRefObject<(() => void) | undefined> = useSynchronizedRef(callback);
	const hasCallback: boolean = isNotNil(callback);

	const stableCallback = useCallback(
		(): void => {
			callbackRef.current?.();
		},
		[callbackRef]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			if (!hasCallback) {
				return;
			}

			let timeoutHandle: SetTimeoutHandle | undefined = undefined;

			const blurHandler = (): void => {
				clearTimeout(timeoutHandle);

				// NOTE focus and blur events trigger on window every time focus changes
				// If blur event is directly followed by focus event this means that the window is still in focus.
				timeoutHandle = setTimeout(
					(): void => {
						stableCallback();
					},
					0
				);
			};

			const focusHandler = (): void => {
				clearTimeout(timeoutHandle);
			};

			const cancellation = new AbortController();

			window.addEventListener("blur", blurHandler, { signal: cancellation.signal });
			window.addEventListener("focus", focusHandler, { signal: cancellation.signal });

			return (): void => {
				clearTimeout(timeoutHandle);
				cancellation.abort();
			};
		},
		[hasCallback, stableCallback]
	);
}